import {
  useSingletonMobileMediaQuery,
  tokens,
} from "@sunrun/experience-ui-components";
import { ReactNode } from "react";
import {
  ReviewCashSummary,
  ReviewPrepaidSummary,
} from "../../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import { formatCurrency } from "../../../utils/formatHelpers";
import {
  PaymentCard,
  PaymentColumn,
  PaymentColumns,
  PaymentGroup,
  PaymentLineItem,
  PaymentRows,
  PaymentValidUntil,
  PaymentGroupTitle,
  PaymentGroupTitleRow,
} from "./OfferReviewPaymentPrimitives";
import { ESTIMATED_UTILITY_BILL_PAYMENTS_TOOLTIP } from "./offerReviewPaymentCopy";
import { OfferReviewInfoTooltip } from "components/atoms/OfferReviewInfoTooltip";
import { SelfSupplyGraphLink } from "components/molecules/offerReviewComparison/SelfSupplySeasonGraphLink";

type PrepaidPaymentDetailsProps = {
  summary: ReviewPrepaidSummary | ReviewCashSummary;
  includedTotal: number;
  showValidUntil: boolean;
  children?: ReactNode;
  onSelfSupplyGraphClick?: () => void;
};

const PrepaidPaymentDetails = ({
  summary,
  includedTotal: _includedTotal,
  showValidUntil,
  children,
  onSelfSupplyGraphClick,
}: PrepaidPaymentDetailsProps) => {
  const isMobile = useSingletonMobileMediaQuery();

  return (
    <PaymentCard $opacity={0.3} data-testid="offer-review-simplified-payment">
      <PaymentColumns $isMobile={isMobile} $columnGap={48}>
        <PaymentColumn>
          <PaymentGroup>
            <PaymentRows>
              <PaymentLineItem
                label="Payment due at signing"
                labelSize={20}
                value={formatCurrency(summary.dueAtSigning, 2)}
                valueSize={20}
                valueWeight={500}
              />
            </PaymentRows>
          </PaymentGroup>
          <PaymentGroup>
            <PaymentGroupTitleRow>
              <PaymentGroupTitle
                size={14}
                fontWeight="medium"
                color={tokens.BRAND_HERO_BLUE}
              >
                Estimated Utility Bill Payments
              </PaymentGroupTitle>
              <OfferReviewInfoTooltip
                content={ESTIMATED_UTILITY_BILL_PAYMENTS_TOOLTIP}
                ariaLabel="About estimated utility bill payments"
                maxWidth={480}
                iconSize={14}
                testId="estimated-utility-bill-tooltip"
              />
            </PaymentGroupTitleRow>
            <PaymentRows>
              <PaymentLineItem
                label="Avg. est. pre-Sunrun utility bill"
                value={formatCurrency(summary.preSolarUtility, 2)}
                unit="/mo"
                valueSize={14}
                valueLineHeight={1.4}
              />
              <PaymentLineItem
                label="Avg. est. post-Sunrun utility bill"
                value={formatCurrency(summary.postSolarUtility, 2)}
                unit="/mo"
                valueSize={14}
                valueLineHeight={1.4}
              />
            </PaymentRows>
          </PaymentGroup>
          {onSelfSupplyGraphClick ? (
            <PaymentGroup>
              <PaymentRows>
                <SelfSupplyGraphLink onClick={onSelfSupplyGraphClick} />
              </PaymentRows>
            </PaymentGroup>
          ) : null}
        </PaymentColumn>
        <PaymentColumn>
          <PaymentGroup>
            <PaymentRows>
              <PaymentLineItem
                label="Payment due at installation"
                value={formatCurrency(summary.dueAtInstallation, 2)}
                valueSize={20}
                valueWeight={500}
                labelSize={20}
              />
            </PaymentRows>
          </PaymentGroup>
          {summary.type === "Prepaid" ? (
            <PaymentGroup>
              <PaymentRows>
                <PaymentGroupTitle
                  size={14}
                  fontWeight="medium"
                  color={tokens.BRAND_HERO_BLUE}
                >
                  Locking in payments with your Agreement Terms:
                </PaymentGroupTitle>
                <PaymentLineItem
                  label="Agreement term"
                  value={String((summary as ReviewPrepaidSummary).termYears)}
                  unit="years"
                />
              </PaymentRows>
            </PaymentGroup>
          ) : null}
        </PaymentColumn>
      </PaymentColumns>
      {children}
      {showValidUntil && <PaymentValidUntil expires={summary.expires} />}
    </PaymentCard>
  );
};

export { PrepaidPaymentDetails };
